import { yupResolver } from '@hookform/resolvers/yup';
import { BlogPostCustomization, IOperationResultNoValue } from '@ViewModels';
import { css } from 'aphrodite';
import equal from 'fast-deep-equal';
import { FC, useEffect } from 'react';
import { FieldErrors, SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { useFindOrCreateAndUpdateBlogStyleMutation } from '../../../../../queries/CustomPageStyle/useFindOrCreateAndUpdateBlogStyleMutation';
import { ColorPicker } from '../../../../../web/components-v2/ColorPicker';
import { bs } from '../../../../../web/styles/styles';
import { useAccountContext } from '../../../../components/blogs';
import { styleSheet } from '../../../../components/blogs/styles';
import { FontSelect } from '../../../../components/FontSelect/FontSelect';
import { Column, Row } from '../../../../components/Layout';
import { ResponsiveFontSizeInputs } from './ResponsiveFontSizeInputs';
import {
	DEFAULT_BLOG_BODY_FONT_SIZE_PX,
	DEFAULT_BLOG_BUTTON_FONT_SIZE_PX,
	DEFAULT_BLOG_POST_TITLE_FONT_SIZE_PX,
	DEFAULT_FONT_VALUES,
	DEFAULT_POST_FORM_VALUES,
	MAX_FONT_SIZE_PX,
	MIN_FONT_SIZE_PX,
} from './constants';
import { blogPostSchema } from './schemas';
import { TemplateFormWrapper } from './TemplateFormWrapper';

interface PostDesignFormProps {
	customContent?: BlogPostCustomization;
	onUpdateSuccess: () => void;
	onFormDirty: () => void;
}

const parseOptionalFontSize = (value: string) => {
	if (value === '' || value === null || value === undefined) {
		return undefined;
	}

	const parsedValue = Number.parseInt(value, 10);
	return Number.isNaN(parsedValue) ? undefined : parsedValue;
};

const getFontSizePlaceholder = (fontSizePx?: number, fallbackFontSizePx?: number) => {
	if (fontSizePx !== undefined && fontSizePx !== null) {
		return String(fontSizePx);
	}

	return fallbackFontSizePx !== undefined ? String(fallbackFontSizePx) : undefined;
};

const mergePostFormValues = (customContent?: BlogPostCustomization): BlogPostCustomization => ({
	...DEFAULT_POST_FORM_VALUES,
	...customContent,
	blogTitle: {
		...DEFAULT_POST_FORM_VALUES.blogTitle,
		...(customContent?.blogTitle ?? {}),
	},
	blogHeadlines: {
		...DEFAULT_POST_FORM_VALUES.blogHeadlines,
		...(customContent?.blogHeadlines ?? {}),
	},
	blogBody: {
		...DEFAULT_POST_FORM_VALUES.blogBody,
		...(customContent?.blogBody ?? {}),
	},
	showMoreButton: {
		...DEFAULT_POST_FORM_VALUES.showMoreButton,
		...(customContent?.showMoreButton ?? {}),
	},
});

export const PostDesignForm: FC<PostDesignFormProps> = ({ customContent, onFormDirty, onUpdateSuccess }) => {
	const errorMessages = useErrorMessages();
	const { accountId, impersonationContext } = useAccountContext();

	const {
		register,
		reset,
		setValue,
		getValues,
		handleSubmit,
		formState: { errors, isDirty },
		watch,
	} = useForm<BlogPostCustomization>({
		defaultValues: DEFAULT_POST_FORM_VALUES,
		mode: 'onBlur',
		resolver: yupResolver(blogPostSchema),
	});
	const blogTitleFont = watch('blogTitle.font', DEFAULT_FONT_VALUES);
	const blogTitleFontSize = watch('blogTitle.fontSizePx');
	const blogHeadlinesFont = watch('blogHeadlines.font', DEFAULT_FONT_VALUES);
	const blogHeadlinesFontSize = watch('blogHeadlines.fontSizePx');
	const blogBodyFont = watch('blogBody.font', DEFAULT_FONT_VALUES);
	const blogBodyFontSize = watch('blogBody.fontSizePx');
	const buttonFont = watch('showMoreButton.font', DEFAULT_FONT_VALUES);
	const buttonFontSize = watch('showMoreButton.fontSizePx');

	// Watch color values for ColorPicker components
	const pageBackgroundColor = watch('blogBody.backgroundColor');
	const buttonBackgroundColor = watch('showMoreButton.backgroundColor');
	const buttonTextColor = watch('showMoreButton.color');
	const buttonHoverColor = watch('showMoreButton.hoverColor');

	const updateBlogPostMutation = useFindOrCreateAndUpdateBlogStyleMutation({
		onSuccess: () => {
			onUpdateSuccess();
			toast.success('Saved changes successfully');
		},
		onError: (error: IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
		},
	});

	const onValid: SubmitHandler<BlogPostCustomization> = (data, event) => {
		event?.preventDefault();
		event?.stopPropagation();

		updateBlogPostMutation.mutate({
			id: accountId,
			impersonationContext,
			style: data,
		});
	};

	const onInvalid = (validationErrors: FieldErrors<BlogPostCustomization>) => {
		const validationErrorMessages = Object.entries(validationErrors)
			.map(([field, error]) => `${field}: ${error?.message || 'invalid'}`)
			.join(', ');
		toast.error(`Validation failed: ${validationErrorMessages}`);
	};

	// Sync customContent to form values when it changes while preserving nested defaults.
	useEffect(() => {
		if (!customContent) {
			return;
		}

		const mergedCustomContent = mergePostFormValues(customContent);
		if (!equal(mergedCustomContent, getValues())) {
			reset(mergedCustomContent);
		}
	}, [customContent, getValues, reset]);

	useEffect(() => {
		if (isDirty) {
			onFormDirty();
		}
	}, [isDirty, onFormDirty]);

	return (
		<TemplateFormWrapper
			title='Customize Blog Post'
			isLoading={updateBlogPostMutation.isPending}
			errors={errors}
			onSubmit={handleSubmit(onValid, onInvalid)}
		>
			<Row className={css(styleSheet.gap24)}>
				<Column className={css(bs.flex1, styleSheet.gap24)}>
					<h2 className={css(styleSheet.subtitleText, bs.flex1)}>Background</h2>
					<div className={css(styleSheet.gap8)}>
						<label className={css(styleSheet.label)}>Page Background Color</label>
						<ColorPicker
							value={pageBackgroundColor || '#ffffff'}
							onChange={color => setValue('blogBody.backgroundColor', color, { shouldDirty: true })}
						/>
					</div>
				</Column>
				<Column className={css(bs.flex1, styleSheet.gap24)}>
					<h2 className={css(styleSheet.subtitleText, bs.flex1)}>Title</h2>
					<FontSelect
						initialValue={customContent?.blogTitle?.font}
						onChange={value => setValue('blogTitle.font', value)}
						errors={errors?.blogTitle?.font}
						value={blogTitleFont}
					/>
					<ResponsiveFontSizeInputs
						desktopError={errors?.blogTitle?.fontSizePx}
						desktopInputProps={{
							placeholder: getFontSizePlaceholder(blogTitleFontSize, DEFAULT_BLOG_POST_TITLE_FONT_SIZE_PX),
							...register('blogTitle.fontSizePx', { setValueAs: parseOptionalFontSize }),
						}}
						desktopLabel='Title Size'
						max={MAX_FONT_SIZE_PX}
						min={MIN_FONT_SIZE_PX}
						mobileError={errors?.blogTitle?.mobileFontSizePx}
						mobileInputProps={{
							placeholder: getFontSizePlaceholder(undefined, blogTitleFontSize ?? DEFAULT_BLOG_POST_TITLE_FONT_SIZE_PX),
							...register('blogTitle.mobileFontSizePx', { setValueAs: parseOptionalFontSize }),
						}}
						mobileLabel='Mobile Title Size'
						step={1}
					/>
				</Column>
				<Column className={css(bs.flex1, styleSheet.gap24)}>
					<h2 className={css(styleSheet.subtitleText, bs.flex1)}>Headlines</h2>
					<FontSelect
						initialValue={customContent?.blogHeadlines?.font}
						onChange={value => setValue('blogHeadlines.font', value)}
						errors={errors?.blogHeadlines?.font}
						value={blogHeadlinesFont}
					/>
					<ResponsiveFontSizeInputs
						desktopError={errors?.blogHeadlines?.fontSizePx}
						desktopInputProps={{
							placeholder: blogHeadlinesFontSize ? String(blogHeadlinesFontSize) : 'Native',
							...register('blogHeadlines.fontSizePx', { setValueAs: parseOptionalFontSize }),
						}}
						desktopLabel='Headlines Size'
						max={MAX_FONT_SIZE_PX}
						min={MIN_FONT_SIZE_PX}
						mobileError={errors?.blogHeadlines?.mobileFontSizePx}
						mobileInputProps={{
							placeholder: 'Native',
							...register('blogHeadlines.mobileFontSizePx', { setValueAs: parseOptionalFontSize }),
						}}
						mobileLabel='Mobile Headlines Size'
						step={1}
					/>
				</Column>
				<Column className={css(bs.flex1, styleSheet.gap24)}>
					<h2 className={css(styleSheet.subtitleText, bs.flex1)}>Body</h2>
					<FontSelect
						initialValue={customContent?.blogBody?.font}
						onChange={value => setValue('blogBody.font', value)}
						errors={errors?.blogBody?.font}
						value={blogBodyFont}
					/>
					<ResponsiveFontSizeInputs
						desktopError={errors?.blogBody?.fontSizePx}
						desktopInputProps={{
							placeholder: getFontSizePlaceholder(blogBodyFontSize, DEFAULT_BLOG_BODY_FONT_SIZE_PX),
							...register('blogBody.fontSizePx', { setValueAs: parseOptionalFontSize }),
						}}
						desktopLabel='Body Size'
						max={MAX_FONT_SIZE_PX}
						min={MIN_FONT_SIZE_PX}
						mobileError={errors?.blogBody?.mobileFontSizePx}
						mobileInputProps={{
							placeholder: getFontSizePlaceholder(undefined, blogBodyFontSize ?? DEFAULT_BLOG_BODY_FONT_SIZE_PX),
							...register('blogBody.mobileFontSizePx', { setValueAs: parseOptionalFontSize }),
						}}
						mobileLabel='Mobile Body Size'
						step={1}
					/>
				</Column>
				<Column className={css(bs.flex1, styleSheet.gap24)}>
					<h2 className={css(styleSheet.subtitleText, bs.flex1)}>Show More Button</h2>
					<FontSelect
						initialValue={customContent?.showMoreButton?.font}
						onChange={value => setValue('showMoreButton.font', value)}
						errors={errors?.showMoreButton?.font}
						value={buttonFont}
					/>
					<ResponsiveFontSizeInputs
						desktopError={errors?.showMoreButton?.fontSizePx}
						desktopInputProps={{
							placeholder: getFontSizePlaceholder(buttonFontSize, DEFAULT_BLOG_BUTTON_FONT_SIZE_PX),
							...register('showMoreButton.fontSizePx', { setValueAs: parseOptionalFontSize }),
						}}
						desktopLabel='Button Text Size'
						max={MAX_FONT_SIZE_PX}
						min={MIN_FONT_SIZE_PX}
						mobileError={errors?.showMoreButton?.mobileFontSizePx}
						mobileInputProps={{
							placeholder: getFontSizePlaceholder(undefined, buttonFontSize ?? DEFAULT_BLOG_BUTTON_FONT_SIZE_PX),
							...register('showMoreButton.mobileFontSizePx', { setValueAs: parseOptionalFontSize }),
						}}
						mobileLabel='Mobile Button Text Size'
						step={1}
					/>
					<div className={css(styleSheet.gap8)}>
						<label className={css(styleSheet.label)}>Color</label>
						<ColorPicker
							value={buttonBackgroundColor || '#599EED'}
							onChange={color => setValue('showMoreButton.backgroundColor', color, { shouldDirty: true })}
						/>
					</div>
					<div className={css(styleSheet.gap8)}>
						<label className={css(styleSheet.label)}>Text Color</label>
						<ColorPicker
							value={buttonTextColor || '#FFFFFF'}
							onChange={color => setValue('showMoreButton.color', color, { shouldDirty: true })}
						/>
					</div>
					<div className={css(styleSheet.gap8)}>
						<label className={css(styleSheet.label)}>Hover Color</label>
						<ColorPicker
							value={buttonHoverColor || '#035697'}
							onChange={color => setValue('showMoreButton.hoverColor', color, { shouldDirty: true })}
						/>
					</div>
				</Column>
			</Row>
		</TemplateFormWrapper>
	);
};
